import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { MeetingNoteTense, TemplateCreationData } from 'src/company/templates/types';
import { SectionEditorLayout } from 'src/company/templates/components/SectionEditorLayout';
import { SectionInstructionsEditorPane } from 'src/company/templates/components/SectionInstructionsEditorPane';
import { useSectionOptions } from 'src/company/templates/hooks/useSectionOptions';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { TemplateToUpdate, useUpdateTemplate } from 'src/company/templates/hooks/useUpdateTemplate';
import { TemplateSection } from 'src/data-capture/hooks/templates/useTemplates';
import { useClients } from 'src/clients/hooks/useClients';
import PageLoader from 'src/UI/loader/PageLoader';
import { v4 as uuidv4 } from 'uuid';

export default function EditSectionInstructionsStep(props: StepProps<TemplateCreationData>) {
  const { data, setData } = props;
  const { sectionOptions } = useSectionOptions();

  const [contentUpdateTimestamp, setContentUpdateTimestamp] = useState(() => Date.now());
  const isApplyingMutationRef = useRef(false);

  const [selectedSectionId, setSelectedSectionId] = useState<string | undefined>(
    props.data.createdTemplate!.sections[0]?.id,
  );
  const { clients: testClients, isLoading: isLoadingClients } = useClients({
    isTest: true,
    companyId: data.company.id,
  });
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoadingClients && testClients.length > 0 && selectedClientId === null) {
      const firstClientId = testClients[0].id;
      setSelectedClientId(firstClientId);
      setData({
        ...data,
        previewClientId: firstClientId,
      });
    }
  }, [testClients, isLoadingClients, selectedClientId]);

  const handleClientSelection = async (clientId: string | null) => {
    setSelectedClientId(clientId);
    setData({
      ...data,
      previewClientId: clientId,
    });

    try {
      await updateTemplateAndUpdateTimestampAndState({
        ...data.createdTemplate!,
        previewClientId: clientId,
      });
    } catch (e) {
      notifyAndLogError(
        'Failed to generate preview for client',
        'Something went wrong while trying to generate the preview for the selected client. Please try again.',
        e,
      );
    }
  };
  const { updateTemplate, invalidateTemplates, isLoading } = useUpdateTemplate();

  const selectedClient = testClients.find(client => client.id === selectedClientId);

  const clientOptions = testClients.map(client => ({
    value: client.id,
    label: `${client.firstName} ${client.lastName}`,
  }));

  const isMeetingNote = data.templateType === 'MEETING_NOTE';

  const tenseOptions = [
    { value: 'THIRD_PERSON', label: 'Third person' },
    { value: 'FIRST_SECOND_PERSON', label: 'First & second person' },
  ];

  const handleTenseChange = (value: string | null) => {
    const newTense = (value as MeetingNoteTense) ?? 'THIRD_PERSON';
    setData({
      ...data,
      meetingNoteTense: newTense,
      createdTemplate: data.createdTemplate
        ? { ...data.createdTemplate, meetingNoteTense: newTense }
        : undefined,
    });
  };

  const sections = data.createdTemplate!.sections;

  const updateSection = useCallback(
    (sectionId: string, updates: Partial<TemplateSection>) => {
      if (isApplyingMutationRef.current) {
        return;
      }
      setData((currentData: TemplateCreationData) => {
        if (!currentData.createdTemplate) {
          return currentData;
        }
        const currentSections = currentData.createdTemplate!.sections;
        const updatedSections = currentSections.map(section =>
          section.id === sectionId ? { ...section, ...updates } : section,
        );
        return {
          ...currentData,
          createdTemplate: {
            ...currentData.createdTemplate!,
            sections: updatedSections,
          },
        };
      });
    },
    [setData],
  );

  const toggleInstructionsComplete = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) {
      return;
    }
    updateSection(sectionId, { isInstructionsComplete: !section.isInstructionsComplete });
  };

  const handleMarkAllComplete = () => {
    setData((currentData: TemplateCreationData) => {
      if (!currentData.createdTemplate) return currentData;
      const updatedSections = currentData.createdTemplate.sections.map(section => ({
        ...section,
        isInstructionsComplete: true,
      }));
      return {
        ...currentData,
        createdTemplate: { ...currentData.createdTemplate, sections: updatedSections },
      };
    });
  };

  const handleSectionHeaderLevelChange = (section: TemplateSection, newLevel: number) => {
    updateSection(section.id, { sectionHeaderLevel: newLevel });
  };

  const handleSectionReorder = (fromIndex: number, toIndex: number) => {
    const newSections = [...sections];
    const [movedSection] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, movedSection);

    setData({
      ...data,
      createdTemplate: {
        ...data.createdTemplate!,
        sections: newSections,
      },
    });
  };

  const handleEditSectionHeader = (section: TemplateSection, newHeader: string) => {
    updateSection(section.id, { sectionHeader: newHeader });
  };

  const handleDeleteSection = (section: TemplateSection) => {
    if (sections.length <= 1) {
      notifyAndLogError(
        'Cannot delete section',
        'A template must have at least one section.',
        null,
      );
      return;
    }

    const newSections = sections.filter(s => s.id !== section.id);
    const deletedIndex = sections.findIndex(s => s.id === section.id);

    setData({
      ...data,
      createdTemplate: {
        ...data.createdTemplate!,
        sections: newSections,
      },
    });

    if (selectedSectionId === section.id) {
      const newSelectedIndex = Math.min(deletedIndex, newSections.length - 1);
      setSelectedSectionId(newSections[newSelectedIndex]?.id);
    }
  };

  const handleAddSection = () => {
    const newSection: TemplateSection = {
      id: uuidv4(),
      templateText: '',
      sectionHeader: 'New Section',
      sectionHeaderLevel: 2,
      sectionInstructions: '',
      preserveAsIs: false,
      isInstructionsComplete: false,
    };

    const newSections = [...sections, newSection];

    setData({
      ...data,
      createdTemplate: {
        ...data.createdTemplate!,
        sections: newSections,
      },
    });

    setSelectedSectionId(newSection.id);
  };

  const handleUpdate = async (section: TemplateSection, message: string) => {
    try {
      const updatingSection = { ...section, editMessage: message, editType: getEditType(section) };
      const templateSections = data.createdTemplate!.sections.map(s =>
        s.id === section.id ? updatingSection : s,
      );

      await updateTemplateAndUpdateTimestampAndState(
        {
          ...data.createdTemplate!,
          sections: templateSections,
          previewClientId: selectedClientId,
          previewSectionId: section.id,
        },
        section.id,
      );
    } catch (error) {
      notifyAndLogError(
        'Failed to update section',
        'An error occurred while trying to update the section. Please try again.',
        error,
        { sectionId: selectedSectionId },
      );
    }
  };

  const getEditType = (section: TemplateSection) => {
    if (!section.isInstructionsLocked && !section.isTemplateLocked) {
      return 'BOTH';
    }
    if (section.isInstructionsLocked) {
      return 'SECTION_TEMPLATE_TEXT';
    }
    if (section.isTemplateLocked) {
      return 'SECTION_INSTRUCTIONS';
    }

    notifyAndLogError(
      'Invalid section lock state',
      'Both section instructions and template cannot be locked at the same time.',
      null,
      { sectionId: section.id },
    );
  };

  const handleNextPress = async () => {
    try {
      await updateTemplate(data.company.id, {
        ...data.createdTemplate!,
        previewClientId: null,
        isActive: true,
      });

      invalidateTemplates(data.company.id);
      props.onSuccess?.();
    } catch (error) {
      notifyAndLogError(
        'Failed to activate template',
        'The template file may still be processing. Please try again in a moment.',
        error,
        { templateId: data.createdTemplate?.id },
      );
    }
  };

  const updateTemplateAndUpdateTimestampAndState = async (
    template: TemplateToUpdate,
    previewSectionId?: string | null,
  ) => {
    const updatedTemplate = await updateTemplate(data.company.id, {
      ...template,
      previewClientId: template.previewClientId,
      previewSectionId: previewSectionId,
    });

    // Block stale editor onChange calls while applying mutation results
    isApplyingMutationRef.current = true;

    setData((currentData: TemplateCreationData) => {
      const sectionIdToPreview =
        currentData.createdTemplate?.sections.reduce(
          (acc, section) => ({
            ...acc,
            [section.id]: {
              sectionPreview: section.sectionPreview,
              isInstructionsComplete: section.isInstructionsComplete,
            },
          }),
          {} as {
            [id: string]: { sectionPreview: string | undefined; isInstructionsComplete?: boolean };
          },
        ) || {};

      if (previewSectionId) {
        updatedTemplate.sections.forEach(section => {
          if (section.id !== previewSectionId) {
            section.sectionPreview = sectionIdToPreview[section.id]?.sectionPreview;
          }
          section.isInstructionsComplete = sectionIdToPreview[section.id]?.isInstructionsComplete;
        });
      }

      return {
        ...currentData,
        createdTemplate: updatedTemplate,
      };
    });
    setContentUpdateTimestamp(Date.now());

    setTimeout(() => {
      isApplyingMutationRef.current = false;
    }, 500);
  };

  const allInstructionsCompleted = sections.every(s => s.isInstructionsComplete);

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Edit section instructions"
      description="Customize the AI instructions for each section to ensure accurate content generation."
      continueText="Continue"
      canGoForward={allInstructionsCompleted}
      hideProgress={true}
      nextStep={handleNextPress}
    >
      {isLoading && <PageLoader />}
      <SectionEditorLayout
        sections={sections}
        selectedSectionId={selectedSectionId}
        onSectionSelect={setSelectedSectionId}
        isSectionCompleted={section => !!section.isInstructionsComplete}
        onToggleComplete={toggleInstructionsComplete}
        onSectionReorder={handleSectionReorder}
        onEditSectionHeader={handleEditSectionHeader}
        onSectionHeaderLevelChange={handleSectionHeaderLevelChange}
        onDeleteSection={handleDeleteSection}
        onAddSection={handleAddSection}
        onMarkAllComplete={handleMarkAllComplete}
        clientSelection={{
          label: 'Test client',
          placeholder: 'Select a test client for preview...',
          data: clientOptions,
          value: selectedClientId,
          onChange: handleClientSelection,
          disabled: isLoadingClients,
        }}
        tenseSelection={
          isMeetingNote
            ? {
                label: 'Writing style',
                description: 'How pronouns are used in meeting notes',
                data: tenseOptions,
                value: data.meetingNoteTense ?? 'THIRD_PERSON',
                onChange: handleTenseChange,
              }
            : undefined
        }
        renderContent={(selectedSection, onToggleComplete) => (
          <SectionInstructionsEditorPane
            section={selectedSection}
            onUpdateSection={updateSection}
            onToggleComplete={onToggleComplete}
            onUpdateWithMessage={handleUpdate}
            isUpdatingMessage={isLoading}
            isCompleted={selectedSection.isInstructionsComplete}
            lastContentExternalUpdateTimestamp={contentUpdateTimestamp}
            placeholderImages={data.extractedImagesCollection?.images || []}
            selectedClient={selectedClient}
            sectionOptions={sectionOptions}
          />
        )}
      />
    </DrawerFlowScreenLayout>
  );
}
